// Login device limit (backend: device_sessions.py).
//
// Teachers may be signed in on 3 devices, students and everyone else on 2.
// A newer login elsewhere signs the oldest device out (websites before the
// mobile apps); that device's next API call answers
//   401 {detail: {code: "session_revoked", message}}.
// Like tenantSuspension.js, one watcher sees every fetch/axios call:
//   - adds this browser's device id (X-Device-Id) to API calls, so signing in
//     again on the same browser reuses its slot instead of taking another;
//   - announces session-ended 401s; SessionEndedGuard signs the user out to
//     their login page and shows the reason.

export const DEVICE_ID_KEY = 'deskey_device_id';
export const SESSION_ENDED_EVENT = 'deskey:session-ended';
const DEVICE_HEADER = 'X-Device-Id';
// session_expired: the access token's time ran out (backend auth_middleware,
// or noticed first by sessionExpiry.js on an idle page).
export const SESSION_EXPIRED_CODE = 'session_expired';
const SESSION_CODES = ['session_revoked', 'session_required', SESSION_EXPIRED_CODE];
const API_BASE = process.env.REACT_APP_BACKEND_URL || '';

/** @param {unknown} detail  API error `detail` */
export const isSessionEnded = (detail) => !!detail && typeof detail === 'object' && SESSION_CODES.includes(detail.code);

const randomId = () => (window.crypto?.randomUUID
  ? window.crypto.randomUUID()
  : `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 12)}`);

/** This browser's stable device id (created on first use). */
export function getDeviceId() {
  try {
    const saved = localStorage.getItem(DEVICE_ID_KEY);
    if (saved) return saved;
    const id = randomId();
    localStorage.setItem(DEVICE_ID_KEY, id);
    return id;
  } catch {
    return null; // storage blocked — every login counts as a new device
  }
}

const urlOf = (input) => (typeof input === 'string' ? input : input?.url || String(input || ''));
const isApiUrl = (url) => (API_BASE ? url.startsWith(API_BASE) : false) || url.startsWith('/api/');

/** fetch(input, init) arguments with the device header added for API calls. */
export function withDeviceHeader(input, init) {
  const deviceId = getDeviceId();
  if (!deviceId || !isApiUrl(urlOf(input))) return [input, init];
  const base = init?.headers ?? (input instanceof Request ? input.headers : undefined);
  const headers = new Headers(base);
  headers.set(DEVICE_HEADER, deviceId);
  return [input, { ...init, headers }];
}

const announce = (detail) => {
  window.dispatchEvent(new CustomEvent(SESSION_ENDED_EVENT, { detail }));
};

/**
 * Install once at startup, before the app renders.
 * @param {import('axios').AxiosStatic} axios  the shared axios instance
 */
export function installDeviceSessionWatcher(axios) {
  if (window.__deskeyDeviceSessionWatcher) return;
  window.__deskeyDeviceSessionWatcher = true;

  const nativeFetch = window.fetch.bind(window);
  window.fetch = async (...args) => {
    const res = await nativeFetch(...withDeviceHeader(...args));
    if (res.status === 401) {
      // Read a copy so the caller can still consume the original body.
      res.clone().json()
        .then((body) => { if (isSessionEnded(body?.detail)) announce(body.detail); })
        .catch(() => { /* non-JSON 401 — not ours */ });
    }
    return res;
  };

  axios.interceptors.request.use((config) => {
    const deviceId = getDeviceId();
    if (deviceId && isApiUrl(config.url || '')) {
      config.headers = config.headers || {};
      config.headers[DEVICE_HEADER] = deviceId;
    }
    return config;
  });
  axios.interceptors.response.use(
    (response) => response,
    (error) => {
      const detail = error?.response?.data?.detail;
      if (error?.response?.status === 401 && isSessionEnded(detail)) announce(detail);
      return Promise.reject(error);
    },
  );
}

/** Tell the server this device signed out, freeing its slot. Fire-and-forget. */
export function endServerSession() {
  let token = null;
  try { token = localStorage.getItem('desky_token'); } catch { /* storage blocked */ }
  if (!token) return;
  window.fetch(`${API_BASE}/api/auth/logout`, {
    method: 'POST',
    headers: new Headers({ Authorization: `Bearer ${token}` }),
    keepalive: true,
  }).catch(() => { /* offline — the session simply expires */ });
}
